import { usePrevious } from "@/hooks";
import { useActiveProjectId } from "@/lib/hooks/useActiveProjectId";
import { useInputStore, useRefInputStore } from "@/state/stores";
import { useLayoutEffect } from "react";

const LINE_HEIGHT = 20; // Default line height in pixels, adjust as needed
const MAX_LINE_COUNT = 10; // Maximum number of lines to display

export function useAutoResizeTextArea() {
  const textareaRef = useRefInputStore(({ inputRef }) => inputRef);
  const messageLength = useInputStore(({ message }) => message.length);
  const activeProjectId = useActiveProjectId();
  const previousActiveProjectId = usePrevious(activeProjectId);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    if (messageLength === 0 || activeProjectId !== previousActiveProjectId) {
      // Reset height and rows if the message is empty or the project ID has changed
      textarea.style.height = "auto";
      textarea.rows = 1;
      return;
    }

    // Reset height temporarily to get the correct scrollHeight
    textarea.style.height = "auto";
    textarea.rows = 1;

    const computedLineHeight =
      Number.parseFloat(getComputedStyle(textarea).lineHeight) || LINE_HEIGHT;

    // Calculate the number of rows based on content
    const calculatedRows = Math.min(
      MAX_LINE_COUNT,
      Math.ceil(textarea.scrollHeight / computedLineHeight),
    );
    textarea.rows = calculatedRows;

    textarea.style.height = `min(calc(${computedLineHeight}px * ${MAX_LINE_COUNT}), ${textarea.scrollHeight}px)`;
  }, [textareaRef, messageLength, activeProjectId, previousActiveProjectId]);
}
